import { useMemo } from 'react';
import { RequestLog } from '../../api/vault';
import * as S from './styles';

interface Props {
	logs: RequestLog[];
	selectedId: number | null;
	onSelect: (log: RequestLog) => void;
}

// Extract the top-level group prefix from a label (e.g., "turn-0", "compaction-context")
// Labels like "turn-0 → generate_designs 0.0 design-notes" → group key "turn-0"
// Labels like "compaction-context ↑" → group key "compaction-context"
const getGroupKey = (label: string): string => {
	// Split on arrow separator first
	const arrowIdx = label.indexOf('→');
	const base = arrowIdx >= 0 ? label.substring(0, arrowIdx).trim() : label.trim();
	// Remove directional markers (↑ ↓ ↑↑ ↓↓)
	return base.replace(/[↑↓]+$/g, '').trim();
};

// Derive tree depth from label structure
const getDepth = (label: string): number => {
	// Count arrow separators for depth
	const arrows = label.split('→').length - 1;
	return arrows;
};

const getDotCategory = (label: string): 'turn' | 'generate' | 'tool' => {
	const hasArrow = label.includes('→');
	const base = label.split('→')[0].trim().replace(/[↑↓]+$/g, '').trim();
	// Top-level turn entries only (no arrow = not nested)
	if (base.startsWith('turn') && !hasArrow) return 'turn';
	// generate_designs and all its children
	if (label.includes('generate_designs') || label.includes('generate-designs')) return 'generate';
	return 'tool';
};

const formatTime = (value: unknown) => {
	if (!value) return '';
	const d = new Date(String(value));
	if (isNaN(d.getTime())) return String(value);
	const hh = String(d.getHours()).padStart(2, '0');
	const mm = String(d.getMinutes()).padStart(2, '0');
	const ss = String(d.getSeconds()).padStart(2, '0');
	const ms = String(d.getMilliseconds()).padStart(3, '0');
	return `${hh}:${mm}:${ss}.${ms}`;
};

// Sort logs so items belonging to the same top-level group stay together.
// Within each group, preserve chronological order.
const sortByGroup = (logs: RequestLog[]): RequestLog[] => {
	// Build group order based on first appearance
	const groupOrder = new Map<string, number>();
	for (const log of logs) {
		const key = getGroupKey(log.label);
		if (!groupOrder.has(key)) {
			groupOrder.set(key, groupOrder.size);
		}
	}

	return [...logs].sort((a, b) => {
		const groupA = groupOrder.get(getGroupKey(a.label)) ?? 0;
		const groupB = groupOrder.get(getGroupKey(b.label)) ?? 0;
		if (groupA !== groupB) return groupA - groupB;
		// Within same group, keep chronological order
		return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
	});
};

const LogTree = ({ logs, selectedId, onSelect }: Props) => {
	//--- HOOKS
	const processedLogs = useMemo(
		() =>
			sortByGroup(logs).map((log) => ({
				...log,
				depth: getDepth(log.label),
			})),
		[logs]
	);

	//--- RENDERING
	if (logs.length === 0) {
		return <S.EmptyState>Select a request to view logs</S.EmptyState>;
	}

	return (
		<S.PanelBody>
			{processedLogs.map((log) => (
				<S.LogRow
					key={log.id}
					$selected={log.id === selectedId}
					$depth={log.depth}
					onClick={() => onSelect(log)}
				>
					<S.LogDot $category={getDotCategory(log.label)} />
					<S.LogLabel title={log.label}>{log.label}</S.LogLabel>
					<S.LogTime>{formatTime(log.createdAt)}</S.LogTime>
				</S.LogRow>
			))}
		</S.PanelBody>
	);
};

export default LogTree;
